import { useState, useEffect, useRef } from 'react';
import { api } from '../../lib/api';
import { bus } from '../../App';
import { formatRelativeTime } from '../../../shared/lib/formatters';
import type { ContextMessage } from '../../../shared/types/context';
import { useSelectionStore } from '../../state/selectionStore';

export function MessagesPanel() {
  const [messages, setMessages] = useState<ContextMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const sessionId = useSelectionStore((state) => state.session_id);
  const messageId = useSelectionStore((state) => state.message_id);
  const select = useSelectionStore((state) => state.set);
  const listRef = useRef<HTMLUListElement | null>(null);
  const lastSessionRef = useRef<string | null>(null);
  const lastCountRef = useRef(0);

  useEffect(() => {
    const unsubscribeDeleted = bus.on('messages/deleted', async (_event, payload) => {
      // Reload messages for the current session if they were deleted
      if (sessionId && payload.session_id === sessionId) {
        try {
          const messageList = await api().services.context.getMessages(sessionId);
          setMessages(messageList || []);
        } catch (error) {
          console.error('[MessagesPanel] Failed to reload messages:', error);
        }
      }
    });

    const unsubscribeCreated = bus.on('message/created', async (_event, payload) => {
      // Reload messages for the current session when a new message is created
      if (sessionId && payload?.session_id === sessionId) {
        try {
          const messageList = await api().services.context.getMessages(sessionId);
          setMessages(messageList || []);
        } catch (error) {
          console.error('[MessagesPanel] Failed to reload messages:', error);
        }
      }
    });

    return () => {
      unsubscribeDeleted();
      unsubscribeCreated();
    };
  }, [sessionId]);

  useEffect(() => {
    let cancelled = false;

    const loadMessages = async () => {
      if (!sessionId) {
        setMessages([]);
        select({ message_id: null });
        setLoading(false);
        return;
      }

      setLoading(true);
      setMessages([]);

      try {
        const messageList = await api().services.context.getMessages(sessionId);
        if (!cancelled) {
          setMessages(messageList || []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[MessagesPanel] Failed to load messages:', error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadMessages();

    return () => {
      cancelled = true;
    };
  }, [sessionId, select]);

  useEffect(() => {
    const checkAndReloadMessages = async () => {
      if (messageId) {
        // Check if the selected message belongs to a different session
        const selectedMessage = messages.find(m => m.id === messageId);

        // If message not found in current messages, it might be from a different session
        if (!selectedMessage && sessionId) {
          try {
            // Fetch the message to get its session_id
            const message = await api().services.context.getMessage(messageId);
            if (message && message.session_id !== sessionId) {
              // Message belongs to different session, reload messages for that session
              const messageList = await api().services.context.getMessages(message.session_id);
              setMessages(messageList || []);
            }
          } catch (error) {
            console.error('[MessagesPanel] Failed to check message session:', error);
          }
        }

        // Use requestAnimationFrame to ensure the DOM is ready
        requestAnimationFrame(() => {
          const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
          if (messageElement) {
            messageElement.scrollIntoView({ block: 'nearest', behavior: 'instant' });
          }
        });
      }
    };

    checkAndReloadMessages();
  }, [messageId, sessionId, messages]);
  useEffect(() => {
    const list = listRef.current;
    if (!list) {
      lastSessionRef.current = sessionId ?? null;
      lastCountRef.current = messages.length;
      return;
    }

    const previousSession = lastSessionRef.current;
    const previousCount = lastCountRef.current;
    const currentSession = sessionId ?? null;
    const sessionChanged = previousSession !== currentSession;
    const appended = messages.length > previousCount;

    if (sessionChanged || appended) {
      requestAnimationFrame(() => {
        const target = listRef.current;
        if (target) {
          target.scrollTop = target.scrollHeight;
        }
      });
    }

    lastSessionRef.current = currentSession;
    lastCountRef.current = messages.length;
  }, [messages, sessionId]);

  const handleMessageClick = (message: ContextMessage) => {
    select({ message_id: message.id });
  };

  const getToolCalls = (message: ContextMessage): Array<{ id: string; name: string }> => {
    try {
      const content = JSON.parse(message.content);
      if (Array.isArray(content)) {
        return content
          .filter((block: any) => block.type === 'tool_use')
          .map((block: any) => ({
            id: block.id || 'unknown',
            name: block.name || 'unknown',
          }));
      }
    } catch {
      // Not JSON or no tool calls
    }
    return [];
  };

  const getPreview = (message: ContextMessage): { text: string; isTextContent: boolean } => {
    try {
      const content = JSON.parse(message.content);
      if (Array.isArray(content)) {
        const textBlock = content.find((block: any) => block.type === 'text');
        if (textBlock && textBlock.text) {
          return {
            text: textBlock.text.substring(0, 100) + (textBlock.text.length > 100 ? '...' : ''),
            isTextContent: true,
          };
        }
      }
      // If no text block found, show one line of JSON
      const jsonStr = JSON.stringify(content);
      return {
        text: jsonStr.substring(0, 100) + (jsonStr.length > 100 ? '...' : ''),
        isTextContent: false,
      };
    } catch {
      // If content is not JSON, use it directly
      return {
        text: message.content.substring(0, 100) + (message.content.length > 100 ? '...' : ''),
        isTextContent: true,
      };
    }
  };

  if (!sessionId) {
    return (
      <div className="panel-shell">
        <div className="panel-content">
          <div className="empty-state">
            <p>No session selected</p>
            <p className="empty-state-subtitle">Select a session to view its messages</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="panel-shell">
        <div className="panel-content">
          <div className="empty-state">
            <p>Loading messages...</p>
          </div>
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="panel-shell">
        <div className="panel-content">
          <div className="empty-state">
            <p>No messages in this session</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="panel-shell">
      <div className="panel-content" style={{ padding: 0 }}>
        <ul
          className="messages-list"
          ref={listRef}
        >
          {messages.map((message, index) => {
            const isSelected = message.id === messageId;
            const previewData = getPreview(message);
            const toolCalls = getToolCalls(message);

            const roleClass = `message-row-role ${
              message.role === 'assistant'
                ? 'message-row-role--assistant'
                : message.role === 'system'
                  ? 'message-row-role--system'
                  : 'message-row-role--user'
            }`;

            // Show bold text for assistant/user when it's actual text content (not JSON)
            const shouldBeBold =
              previewData.isTextContent &&
              (message.role === 'assistant' || message.role === 'user');
            const previewClass = `message-row-preview ${shouldBeBold ? 'message-row-preview--text' : ''}`;

            return (
              <li key={message.id}>
                <button
                  type="button"
                  onClick={() => handleMessageClick(message)}
                  className={`message-row ${index % 2 === 0 ? 'message-row--even' : 'message-row--odd'} ${isSelected ? 'message-row--selected' : ''}`}
                  data-message-id={message.id}
                >
                  <div className="message-row-header">
                    <span className="message-row-id">#{message.id}</span>
                    <span className="message-row-date">{formatRelativeTime(new Date(message.timestamp).toISOString(), '—')}</span>
                    <span className={roleClass}>{message.role}</span>
                    {toolCalls.length > 0 && (
                      <div className="message-row-tools">
                        {toolCalls.map((tool) => (
                          <span key={tool.id} className="badge badge--positive">
                            {tool.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className={previewClass}>{previewData.text}</p>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
